import { cyclesOn, MONTHLY_DAYS, monthlyCycles, nextCycleOnOrAfter } from './round-config'
import type { Clean, Customer } from './types'

// Several jobs per customer (0042; docs/plans/several-jobs/PLAN.md v3, Liam's answers 2 Oct 2026). The phone's half
// of the ONE Job shape (the server's is functions/lib/jobs.ts; keep the two in step).
//
// Job 1 lives on the customer row (price, how often, job_name, job_stopped_on) and owns every visit with no job_id.
// Extra jobs are customer_jobs rows. So no screen keeps a second scheduling engine, every scheduling question takes
// a Job plus THAT job's visits only: due date, the next visit, "one upcoming visit", the gutters-before order.
// Nothing here draws anything; step 2's screens are built on it.

export type JobKind = 'window' | 'gutter' | 'other'

export interface Job {
  id: number | null // null = job 1, on the customer row
  customer_id: number
  name: string | null // null on job 1 = the trade's word ("Windows")
  kind: JobKind | null // null on job 1 = whatever its visits say
  price_pence: number | null
  first_price_pence: number | null // job 1 only: the first-clean ("ad-hoc") price
  cycle_days: number | null // null = no repeat
  cycle_text: string | null
  active: boolean // false = removed
  stopped_on: string | null
}

// A customer_jobs row as GET /api/customers/:id/jobs and GET /api/customer-jobs answer it.
export interface CustomerJob {
  message_record_id?: string | null
  id: number
  customer_id: number
  name: string
  kind: JobKind
  price_pence: number | null
  cycle_days: number | null
  cycle_text: string | null
  stopped_on: string | null
  rebook_messages: number
  rebook_messaged_on: string | null
  // Due's "Next week" for this job (0063). Optional: a saved copy from before lacks them.
  due_pushed_until?: string | null
  due_pushes?: number
  notes: string | null
  version: number
  deleted_at: string | null
  // That job's own dates (GET /api/customer-jobs), worked out from its visits only.
  last_clean?: string | null
  last_clean_id?: number | null
  last_clean_version?: number | null
  next_booked?: string | null
  next_booked_id?: number | null
  next_booked_version?: number | null
  next_booked_time_slot?: string | null
  next_booked_time_agreed_for?: string | null
  next_booked_created_by?: string | null
  next_booked_source?: string | null
  next_booked_confirmed?: number | null
  next_confirmed?: string | null
  has_unconfirmed?: number | null
  missed_visit_date?: string | null
  missed_visit_id?: number | null
  missed_visit_version?: number | null
  missed_visit_confirmed?: number | null
  missed_visit_time_slot?: string | null
  missed_visit_time_agreed_for?: string | null
  overdue_bookings?: number
}

const positive = (v: number | null | undefined): number | null => (typeof v === 'number' && v > 0 ? v : null)

export function jobOneOf(c: Pick<Customer, 'id' | 'reg_price_pence' | 'first_clean_price_pence' | 'cycle_days' | 'cycle_text' | 'deleted_at'> & { job_name?: string | null; job_stopped_on?: string | null }): Job {
  return {
    id: null,
    customer_id: c.id,
    name: c.job_name ?? null,
    kind: null,
    price_pence: c.reg_price_pence ?? null,
    first_price_pence: c.first_clean_price_pence ?? null,
    cycle_days: positive(c.cycle_days),
    cycle_text: c.cycle_text ?? null,
    active: !c.deleted_at,
    stopped_on: c.job_stopped_on ?? null,
  }
}

export function extraJobOf(j: CustomerJob): Job {
  return {
    id: j.id,
    customer_id: j.customer_id,
    name: j.name,
    kind: j.kind,
    price_pence: j.price_pence ?? null,
    first_price_pence: null,
    cycle_days: positive(j.cycle_days),
    cycle_text: j.cycle_text ?? null,
    active: !j.deleted_at,
    stopped_on: j.stopped_on ?? null,
  }
}

// A customer's jobs: job 1 first, then their live extra jobs in the order they were added.
export function jobsOf(c: Parameters<typeof jobOneOf>[0], extra: CustomerJob[]): Job[] {
  return [jobOneOf(c), ...extra.filter((j) => j.customer_id === c.id && !j.deleted_at).sort((a, b) => a.id - b.id).map(extraJobOf)]
}

export function isRunning(job: Job): boolean {
  return job.active && job.stopped_on === null
}

// The job a visit belongs to. A visit from before several jobs carries no job_id: job 1's.
export function visitJobId(v: { job_id?: number | null }): number | null {
  return typeof v.job_id === 'number' ? v.job_id : null
}

export function isJobVisit(job: Pick<Job, 'id' | 'customer_id'>, v: Pick<Clean, 'customer_id'> & { job_id?: number | null }): boolean {
  return v.customer_id === job.customer_id && visitJobId(v) === job.id
}

// That job's visits only, live ones.
export function visitsOf<V extends Pick<Clean, 'customer_id' | 'deleted_at'> & { job_id?: number | null }>(job: Pick<Job, 'id' | 'customer_id'>, cleans: V[]): V[] {
  return cleans.filter((v) => !v.deleted_at && isJobVisit(job, v))
}

type DatedVisit = Pick<Clean, 'customer_id' | 'deleted_at' | 'state' | 'date' | 'id'> & { job_id?: number | null }

export function lastDoneOf(job: Pick<Job, 'id' | 'customer_id'>, cleans: DatedVisit[]): string | null {
  let best: string | null = null
  for (const v of visitsOf(job, cleans)) if (v.state === 'done' && (best === null || v.date > best)) best = v.date
  return best
}

// "One upcoming visit per job" (PLAN 4): the job's earliest booked visit from today on.
export function nextBookedOf<V extends DatedVisit>(job: Pick<Job, 'id' | 'customer_id'>, cleans: V[], today: string): V | null {
  let best: V | null = null
  for (const v of visitsOf(job, cleans)) {
    if (v.state !== 'booked' || v.date < today) continue
    if (!best || v.date < best.date || (v.date === best.date && v.id < best.id)) best = v
  }
  return best
}


// When the job is next due: its own last done visit plus its own cycle. No repeat, or nothing done yet: no date.
export function dueDateOf(job: Job, cleans: DatedVisit[]): string | null {
  const last = lastDoneOf(job, cleans)
  return last && job.cycle_days ? cyclesOn(last, job.cycle_days) : null
}

// The date Roundly books after a Done (functions/lib/autobook.ts nextDueDate): one cycle on, rolled forward in
// whole cycles until it is today or later.
export function nextAfterDone(doneDate: string, cycleDays: number, today: string): string {
  return nextCycleOnOrAfter(doneDate, cycleDays, today)
}

// Visits at one house on one day (Liam Q11: "almost always people have their gutters done before their windows,
// because sometimes you get stuff down the windows"): gutter-kind jobs first, then other jobs, then window-kind
// jobs, then the day's own order. Returns a new array.
const KIND_ORDER: Record<JobKind, number> = { gutter: 0, other: 1, window: 2 }
// A visit with no kind (a copy saved before kinds were sent) counts as windows.
export function gutterFirst<V extends { kind?: JobKind | null }>(visits: V[], dayOrder: (a: V, b: V) => number = () => 0): V[] {
  return [...visits].sort((a, b) => (KIND_ORDER[a.kind ?? 'window'] ?? 1) - (KIND_ORDER[b.kind ?? 'window'] ?? 1) || dayOrder(a, b))
}

// The day Roundly suggests for a gutters visit near a windows one (Liam Q1, Q11): the windows day itself, never a
// day after it. null when the windows visit is more than `window` days away from when the gutters are due.
export function gutterDayNear(gutterDue: string, windowsDay: string, window = 14): string | null {
  const gap = Math.round((Date.parse(`${windowsDay}T12:00:00Z`) - Date.parse(`${gutterDue}T12:00:00Z`)) / 86_400_000)
  return Math.abs(gap) <= window ? windowsDay : null
}

// What the job is called on a card: its own name, else the trade's word for its kind (job 1: "Windows").
export function jobWord(job: Pick<Job, 'name' | 'kind'>, kindWord: (kind: JobKind | null) => string): string {
  return job.name?.trim() || kindWord(job.kind)
}

// "every 4 weeks", "every 26 weeks", "every 3 days"; null for no repeat.
export function everyWords(days: number | null | undefined): string | null {
  if (!days || days <= 0) return null
  if (days === MONTHLY_DAYS && monthlyCycles()) return 'every month'
  if (days % 7 === 0) return days === 7 ? 'every week' : `every ${days / 7} weeks`
  if (days === 365) return 'every year'
  return `every ${days} days`
}

// The first day Roundly suggests for a new job (Liam Q11): the owner picks, Roundly suggests. Gutters (and any
// other extra job) go on the day the windows are next booked, never after it; with nothing booked for the windows,
// the day they are due if that is still to come; else today.
export function suggestFirstDate(windowsNext: string | null | undefined, windowsDue: string | null | undefined, today: string): { date: string; why: 'windows' | 'due' | 'today' } {
  if (windowsNext && windowsNext >= today) return { date: windowsNext, why: 'windows' }
  if (windowsDue && windowsDue >= today) return { date: windowsDue, why: 'due' }
  return { date: today, why: 'today' }
}

// The booked visits a stop asks about (functions/api/customers/[id]/jobs/stop.ts ASKED): the job's booked visits
// from today on that a person placed or the customer agreed. The app's own unagreed suggestions are not asked
// about; the stop takes them away itself.
export function stopAsksAbout<V extends Pick<Clean, 'customer_id' | 'deleted_at' | 'state' | 'date' | 'id' | 'confirmed' | 'source'> & { job_id?: number | null }>(
  job: Pick<Job, 'id' | 'customer_id'>,
  cleans: V[],
  today: string,
): V[] {
  return visitsOf(job, cleans)
    .filter((v) => v.state === 'booked' && v.date >= today && (v.confirmed === 1 || v.source === null || (v.source !== 'suggested' && v.source !== 'tentative')))
    .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
}

// One house, one place in the day (Liam Q9, Q11): a customer's visits on a day are pulled together where the first
// of them stands, gutters first, then other jobs, then windows. Everyone else keeps the day's own order. Returns a
// new array; a day with one visit per house comes back in the same order.
export function houseOrder<V extends { customer_id: number; kind?: JobKind | null }>(visits: V[]): V[] {
  const out: V[] = []
  const seen = new Set<number>()
  for (const v of visits) {
    if (seen.has(v.customer_id)) continue
    seen.add(v.customer_id)
    const same = visits.filter((x) => x.customer_id === v.customer_id)
    out.push(...(same.length > 1 ? gutterFirst(same, (a, b) => visits.indexOf(a) - visits.indexOf(b)) : same))
  }
  return out
}

// Should the diary ask to move a gutters visit onto the windows day (Liam Q1, Q11)? Gutters go on the windows day
// or the day before it, never after. Within `window` days either side of the windows visit and on neither of those
// two days: ask, offering the windows day. Otherwise nothing to ask.
export function gutterAsk(gutterDate: string, windowsDay: string, window = 14): string | null {
  const gap = Math.round((Date.parse(`${windowsDay}T12:00:00Z`) - Date.parse(`${gutterDate}T12:00:00Z`)) / 86_400_000)
  if (gap === 0 || gap === 1) return null
  return Math.abs(gap) <= window ? windowsDay : null
}

// Today's stops (Liam Q9): with several jobs on, a house's jobs on the day are one stop, gutters first, where the
// first of them stands; off, every visit is its own stop in the day's order, exactly as before.
export function dayStops<V extends { customer_id: number; kind?: JobKind | null }>(ordered: V[], severalJobs: boolean): V[][] {
  if (!severalJobs) return ordered.map((v) => [v])
  const out: V[][] = []
  for (const v of houseOrder(ordered)) {
    const last = out[out.length - 1]
    if (last && last[0]!.customer_id === v.customer_id) last.push(v)
    else out.push([v])
  }
  return out
}

// Gutters after the windows (Liam Q11: "never the day after"): a gutters day from one to `window` days after a
// windows day already booked. Roundly never suggests one, and a person choosing one is stopped and offered the
// windows day instead; an existing one is only ever moved with the owner's yes.
export function guttersAfterWindows(gutterDate: string, windowsDay: string, window = 14): boolean {
  const gap = Math.round((Date.parse(`${gutterDate}T12:00:00Z`) - Date.parse(`${windowsDay}T12:00:00Z`)) / 86_400_000)
  return gap >= 1 && gap <= window
}

// The windows day a gutters day would fall after, from the customer's booked main-job visits (from today on).
export function windowsDayBefore<V extends Pick<Clean, 'state' | 'date' | 'deleted_at'> & { job_id?: number | null }>(gutterDate: string, visits: V[], today: string): string | null {
  const days = visits.filter((v) => !v.deleted_at && v.state === 'booked' && visitJobId(v) === null && v.date >= today).map((v) => v.date).sort()
  for (const d of days) if (guttersAfterWindows(gutterDate, d)) return d
  return null
}
